
import { useState } from 'react';
import { useForm } from 'react-hook-form';

interface WarrantyClaim {
  id: string;
  orderNo: string;
  customer: string;
  product: string;
  purchaseDate: string;
  warrantyPeriod: number;
  issueDescription: string;
  status: 'pending' | 'approved' | 'repaired' | 'replaced' | 'rejected';
  assignedTo?: string;
  createdAt: string;
  updatedAt: string;
}

// Mock warranty claims data
const mockWarrantyClaims: WarrantyClaim[] = [
  {
    id: 'WC001',
    orderNo: 'ORD-2024-001',
    customer: 'Rajesh Kumar',
    product: 'Royal Diamond Ring',
    purchaseDate: '2024-01-15',
    warrantyPeriod: 12,
    issueDescription: 'Stone loosening in the setting',
    status: 'pending',
    createdAt: '2024-01-20T10:00:00Z',
    updatedAt: '2024-01-20T10:00:00Z'
  },
  {
    id: 'WC002',
    orderNo: 'ORD-2024-002',
    customer: 'Priya Sharma',
    product: 'Classic Gold Chain',
    purchaseDate: '2024-01-16',
    warrantyPeriod: 24,
    issueDescription: 'Chain link broken',
    status: 'approved',
    assignedTo: 'Technician A',
    createdAt: '2024-01-21T11:00:00Z',
    updatedAt: '2024-01-21T11:00:00Z'
  },
  {
    id: 'WC003',
    orderNo: 'ORD-2024-003',
    customer: 'Amit Patel',
    product: 'Pearl Drop Earrings',
    purchaseDate: '2024-01-17',
    warrantyPeriod: 6,
    issueDescription: 'Pearl detached from setting',
    status: 'repaired',
    assignedTo: 'Technician B',
    createdAt: '2024-01-22T12:00:00Z',
    updatedAt: '2024-01-22T12:00:00Z'
  },
  {
    id: 'WC004',
    orderNo: 'ORD-2024-004',
    customer: 'Sunita Reddy',
    product: 'Tennis Bracelet',
    purchaseDate: '2024-01-18',
    warrantyPeriod: 18,
    issueDescription: 'Clasp mechanism not working properly',
    status: 'replaced',
    assignedTo: 'Technician C',
    createdAt: '2024-01-23T13:00:00Z',
    updatedAt: '2024-01-23T13:00:00Z'
  },
  {
    id: 'WC005',
    orderNo: 'ORD-2024-005',
    customer: 'Vikram Singh',
    product: 'Heart Pendant',
    purchaseDate: '2024-01-19',
    warrantyPeriod: 12,
    issueDescription: 'Ruby stone has visible crack',
    status: 'rejected',
    createdAt: '2024-01-24T14:00:00Z',
    updatedAt: '2024-01-24T14:00:00Z'
  }
];

export default function WarrantyClaims() {
  const [claims, setClaims] = useState<WarrantyClaim[]>(mockWarrantyClaims);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const { register, handleSubmit, reset } = useForm();
  const [showForm, setShowForm] = useState(false);
  const [selectedClaim, setSelectedClaim] = useState<WarrantyClaim | null>(null);

  const onSubmit = async (data: any) => {
    try {
      setIsLoading(true);
      const newClaim: WarrantyClaim = {
        ...data,
        id: `WC${String(Date.now()).slice(-3)}`,
        status: 'pending',
        createdAt: new Date().toISOString(),
        updatedAt: new Date().toISOString()
      };
      setClaims(prev => [...prev, newClaim]);
      reset();
      setShowForm(false);
    } catch (error) {
      console.error('Error adding claim:', error);
      setError('Failed to create warranty claim');
    } finally {
      setIsLoading(false);
    }
  };

  const updateClaimStatus = async (id: string, status: string, assignedTo?: string) => {
    try {
      setClaims(prev => prev.map(claim => 
        claim.id === id 
          ? { ...claim, status: status as WarrantyClaim['status'], assignedTo, updatedAt: new Date().toISOString() }
          : claim
      ));
    } catch (error) {
      console.error('Error updating claim:', error);
      setError('Failed to update claim status');
    }
  };

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'pending': return 'bg-yellow-100 text-yellow-800';
      case 'approved': return 'bg-blue-100 text-blue-800';
      case 'repaired': return 'bg-green-100 text-green-800';
      case 'replaced': return 'bg-purple-100 text-purple-800';
      case 'rejected': return 'bg-red-100 text-red-800';
      default: return 'bg-gray-100 text-gray-800';
    }
  };

  const isWarrantyValid = (purchaseDate: string, warrantyPeriod: number) => {
    const purchase = new Date(purchaseDate);
    const expiry = new Date(purchase.setMonth(purchase.getMonth() + warrantyPeriod));
    return new Date() <= expiry;
  };

  if (isLoading && claims.length === 0) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
        <span className="ml-3 text-gray-600">Loading warranty claims...</span>
      </div>
    );
  }

  if (error) {
    return (
      <div className="bg-red-50 border border-red-200 rounded-lg p-4">
        <p className="text-red-800">Error: {error}</p>
        <button 
          onClick={() => setError(null)}
          className="mt-2 text-red-600 hover:text-red-800 text-sm underline cursor-pointer"
        >
          Try again
        </button>
      </div>
    );
  }

  return (
    <div className="p-6 space-y-6">
      <div className="flex justify-between items-center">
        <h1 className="text-2xl font-semibold text-[var(--color-primary)]">Warranty Claims</h1>
        <button
          onClick={() => setShowForm(true)}
          className="bg-[var(--color-primary)] text-white px-4 py-2 rounded-lg hover:bg-[var(--color-primary)]/90 transition-colors whitespace-nowrap cursor-pointer"
        >
          New Claim
        </button>
      </div>

      {/* Statistics Cards */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
        <div className="bg-white p-4 rounded-lg shadow border-l-4 border-yellow-500">
          <h3 className="text-sm font-medium text-gray-600">Pending Claims</h3>
          <p className="text-2xl font-bold text-yellow-600">
            {claims.filter(c => c.status === 'pending').length}
          </p>
        </div>
        <div className="bg-white p-4 rounded-lg shadow border-l-4 border-blue-500">
          <h3 className="text-sm font-medium text-gray-600">Approved Claims</h3>
          <p className="text-2xl font-bold text-blue-600">
            {claims.filter(c => c.status === 'approved').length}
          </p>
        </div>
        <div className="bg-white p-4 rounded-lg shadow border-l-4 border-green-500">
          <h3 className="text-sm font-medium text-gray-600">Completed</h3>
          <p className="text-2xl font-bold text-green-600">
            {claims.filter(c => ['repaired', 'replaced'].includes(c.status)).length}
          </p>
        </div>
        <div className="bg-white p-4 rounded-lg shadow border-l-4 border-red-500">
          <h3 className="text-sm font-medium text-gray-600">Rejected</h3>
          <p className="text-2xl font-bold text-red-600">
            {claims.filter(c => c.status === 'rejected').length}
          </p>
        </div>
      </div>

      {/* Claims Table */}
      <div className="bg-white rounded-lg shadow overflow-hidden">
        {claims.length === 0 ? (
          <div className="p-8 text-center">
            <i className="ri-shield-check-line text-4xl text-gray-300 mb-4"></i>
            <p className="text-gray-500 mb-2">No warranty claims yet</p>
            <p className="text-sm text-gray-400">Create your first warranty claim to get started</p>
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="min-w-full divide-y divide-gray-200">
              <thead className="bg-[var(--color-accent)]/20">
                <tr>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Claim ID
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Order No
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Customer
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Product
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Issue
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Warranty Status
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Status
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Actions
                  </th>
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-gray-200">
                {claims.map((claim) => (
                  <tr key={claim.id} className="hover:bg-gray-50">
                    <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900">
                      {claim.id}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                      {claim.orderNo}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                      {claim.customer}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                      {claim.product}
                    </td>
                    <td className="px-6 py-4 text-sm text-gray-900 max-w-xs truncate">
                      {claim.issueDescription}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm">
                      <span className={`px-2 py-1 rounded-full text-xs font-medium ${
                        isWarrantyValid(claim.purchaseDate, claim.warrantyPeriod)
                          ? 'bg-green-100 text-green-800'
                          : 'bg-red-100 text-red-800'
                      }`}>
                        {isWarrantyValid(claim.purchaseDate, claim.warrantyPeriod) ? 'Valid' : 'Expired'}
                      </span>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm">
                      <span className={`px-2 py-1 rounded-full text-xs font-medium ${getStatusColor(claim.status)}`}>
                        {claim.status}
                      </span>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm font-medium space-x-2">
                      {claim.status === 'pending' && (
                        <>
                          <button
                            onClick={() => updateClaimStatus(claim.id, 'approved')}
                            className="text-green-600 hover:text-green-900 cursor-pointer"
                          >
                            Approve
                          </button>
                          <button
                            onClick={() => updateClaimStatus(claim.id, 'rejected')}
                            className="text-red-600 hover:text-red-900 cursor-pointer"
                          >
                            Reject
                          </button>
                        </>
                      )}
                      <button
                        onClick={() => setSelectedClaim(claim)}
                        className="text-[var(--color-primary)] hover:text-[var(--color-primary)]/80 cursor-pointer"
                      >
                        View
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {/* New Claim Modal */}
      {showForm && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg p-6 w-full max-w-md">
            <h2 className="text-xl font-semibold mb-4">New Warranty Claim</h2>
            <form onSubmit={handleSubmit(onSubmit)} className="space-y-4">
              <input
                {...register('orderNo', { required: true })}
                placeholder="Order Number"
                className="w-full p-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[var(--color-primary)] focus:border-transparent"
              />
              <input
                {...register('customer', { required: true })}
                placeholder="Customer Name"
                className="w-full p-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[var(--color-primary)] focus:border-transparent"
              />
              <input
                {...register('product', { required: true })}
                placeholder="Product Name"
                className="w-full p-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[var(--color-primary)] focus:border-transparent"
              />
              <input
                {...register('purchaseDate', { required: true })}
                type="date"
                className="w-full p-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[var(--color-primary)] focus:border-transparent"
              />
              <input
                {...register('warrantyPeriod', { required: true })}
                type="number"
                placeholder="Warranty Period (months)"
                className="w-full p-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[var(--color-primary)] focus:border-transparent"
              />
              <textarea
                {...register('issueDescription', { required: true })}
                placeholder="Issue Description"
                rows={3}
                className="w-full p-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[var(--color-primary)] focus:border-transparent"
              />
              <div className="flex space-x-3">
                <button
                  type="submit"
                  disabled={isLoading}
                  className="flex-1 bg-[var(--color-primary)] text-white py-2 px-4 rounded-lg hover:bg-[var(--color-primary)]/90 disabled:opacity-50 whitespace-nowrap cursor-pointer"
                >
                  {isLoading ? 'Creating...' : 'Create Claim'}
                </button>
                <button
                  type="button"
                  onClick={() => setShowForm(false)}
                  className="flex-1 bg-gray-300 text-gray-700 py-2 px-4 rounded-lg hover:bg-gray-400 whitespace-nowrap cursor-pointer"
                >
                  Cancel
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Claim Detail Modal */}
      {selectedClaim && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg p-6 w-full max-w-2xl">
            <div className="flex justify-between items-center mb-4">
              <h2 className="text-xl font-semibold">Claim Details</h2>
              <button
                onClick={() => setSelectedClaim(null)}
                className="text-gray-500 hover:text-gray-700 cursor-pointer"
              >
                ✕
              </button>
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-700">Claim ID</label>
                <p className="mt-1 text-sm text-gray-900">{selectedClaim.id}</p>
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700">Order Number</label>
                <p className="mt-1 text-sm text-gray-900">{selectedClaim.orderNo}</p>
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700">Customer</label>
                <p className="mt-1 text-sm text-gray-900">{selectedClaim.customer}</p>
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700">Product</label>
                <p className="mt-1 text-sm text-gray-900">{selectedClaim.product}</p>
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700">Purchase Date</label>
                <p className="mt-1 text-sm text-gray-900">{new Date(selectedClaim.purchaseDate).toLocaleDateString()}</p>
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700">Warranty Period</label>
                <p className="mt-1 text-sm text-gray-900">{selectedClaim.warrantyPeriod} months</p>
              </div>
              <div className="col-span-2">
                <label className="block text-sm font-medium text-gray-700">Issue Description</label>
                <p className="mt-1 text-sm text-gray-900">{selectedClaim.issueDescription}</p>
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700">Status</label>
                <span className={`mt-1 inline-block px-2 py-1 rounded-full text-xs font-medium ${getStatusColor(selectedClaim.status)}`}>
                  {selectedClaim.status}
                </span>
              </div>
              {selectedClaim.assignedTo && (
                <div>
                  <label className="block text-sm font-medium text-gray-700">Assigned To</label>
                  <p className="mt-1 text-sm text-gray-900">{selectedClaim.assignedTo}</p>
                </div>
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
